import Client from "../client";
import type {
  Account,
  AccountItem,
  AttachOIDC,
  AttachedOIDC,
  AuditLog,
  ChangePasswordParams,
  CreateAccountParams,
  CreateAccountUserResponse,
  CreateServiceUserResponse,
  Credentials,
  ForgotPasswordParams,
  InviteUserParams,
  InviteUserResponse,
  ModeName,
  OIDC,
  RequestOptions,
  StatusResponse,
  Terms,
  UpdateCurrentUserParams,
  UpdateUserParams,
} from "../types";

export default class IAMController {
  #client: Client;

  constructor(client: Client) {
    this.#client = client;
  }

  async newAccountUser(
    { email, name, company }: CreateAccountParams,
    options: RequestOptions = {},
  ): Promise<CreateAccountUserResponse> {
    const response = await this.#client.request("/iam/new_user_and_account", {
      method: "POST",
      body: JSON.stringify({
        email,
        name,
        company,
      }),
      ...options,
    });

    return (await response.json()) as CreateAccountUserResponse;
  }

  /** Delete an account and all things in it */
  async purgeAccount(options: RequestOptions = {}): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/iam/account", {
      method: "DELETE",
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  /** Update properties on an account */
  async updateAccount(
    {
      mode,
      company,
      slug,
    }: { mode?: ModeName; company?: string; slug?: string },
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/iam/account", {
      method: "PUT",
      body: JSON.stringify({ mode, company, slug }),
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  /** Get account properties */
  async getAccount(options: RequestOptions = {}) {
    const response = await this.#client.requestWithAuth("/iam/account", {
      method: "GET",
      ...options,
    });

    return (await response.json()) as Account;
  }

  /** List all accounts */
  async listAccounts(options: RequestOptions = {}): Promise<AccountItem[]> {
    const response = await this.#client.requestWithToken("/iam/user/account", {
      method: "GET",
      ...options,
    });

    return (await response.json()) as AccountItem[];
  }

  /** Exchange verification token for bearer token */
  async exchangeToken(
    token: string,
    options: RequestOptions = {},
  ): Promise<Credentials & { handle: string }> {
    const searchParams = new URLSearchParams({
      token,
    });
    const response = await this.#client.request(
      `/iam/account/login?${searchParams.toString()}`,
      {
        method: "GET",
        ...options,
      },
    );

    const json = (await response.json()) as {
      account_id: string;
      token: string;
      handle: string;
    };

    return {
      account: json.account_id,
      token: json.token,
      handle: json.handle,
    };
  }

  /** Get the OIDC provider name */
  async getOIDC(
    slug: string,
    options: RequestOptions = {},
  ): Promise<{ oidc: string | null }> {
    const searchParams = new URLSearchParams({
      slug,
    });
    const response = await this.#client.request(
      `/iam/account/oidc?${searchParams.toString()}`,
      {
        method: "GET",
        ...options,
      },
    );

    return (await response.json()) as { oidc: string | null };
  }

  /** Create a new user inside an account */
  async inviteUser(
    { permission, email, name, oidc }: InviteUserParams,
    options: RequestOptions = {},
  ): Promise<InviteUserResponse> {
    const response = await this.#client.requestWithAuth("/iam/account/user", {
      method: "POST",
      body: JSON.stringify({
        permission,
        handle: email,
        name,
        oidc,
      }),
      ...options,
    });

    return (await response.json()) as InviteUserResponse;
  }

  async forgotPassword(
    params: ForgotPasswordParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.request("/iam/user/forgot_password", {
      method: "POST",
      body: JSON.stringify(params),
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  async changePassword(
    params: ChangePasswordParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      "/iam/user/change_password",
      {
        method: "POST",
        body: JSON.stringify(params),
        ...options,
      },
    );

    return (await response.json()) as StatusResponse;
  }

  /** Create a new service user inside an account */
  async createServiceUser(
    name?: string,
    options: RequestOptions = {},
  ): Promise<CreateServiceUserResponse> {
    const response = await this.#client.requestWithAuth(
      "/iam/account/service_user",
      {
        method: "POST",
        body: JSON.stringify({
          name,
        }),
        ...options,
      },
    );

    return (await response.json()) as CreateServiceUserResponse;
  }

  async deleteServiceUser(
    handle: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      "/iam/account/service_user",
      {
        method: "DELETE",
        body: JSON.stringify({
          handle,
        }),
        ...options,
      },
    );

    return (await response.json()) as StatusResponse;
  }

  /** Update properties on a user */
  async updateUser(
    params: UpdateUserParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/iam/account/user", {
      method: "PUT",
      body: JSON.stringify(params),
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  /** Update properties on the current user */
  async updateCurrentUser(
    params: UpdateCurrentUserParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/iam/user", {
      method: "PUT",
      body: JSON.stringify(params),
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  /** Delete a user from an account */
  async deleteUser(
    { handle, oidc }: { handle: string; oidc: OIDC },
    options: RequestOptions = {},
  ): Promise<{ handle: string; oidc: string; removed: boolean }> {
    const searchParams = new URLSearchParams();

    searchParams.set("handle", handle);
    searchParams.set("oidc", oidc ?? "");

    const response = await this.#client.requestWithAuth(
      `/iam/account/user?${searchParams.toString()}`,
      {
        method: "DELETE",
        ...options,
      },
    );

    return (await response.json()) as {
      handle: string;
      oidc: string;
      removed: boolean;
    };
  }

  /** Get audit logs from the last X days */
  async auditLogs(
    days: number = 7,
    limit: number = 1000,
    options: RequestOptions = {},
  ): Promise<AuditLog[]> {
    const searchParams = new URLSearchParams({
      days: days.toString(),
      limit: limit.toString(),
    });
    const response = await this.#client.requestWithAuth(
      `/iam/audit?${searchParams.toString()}`,
      {
        method: "GET",
        ...options,
      },
    );

    return (await response.json()) as AuditLog[];
  }

  async attachOIDC(
    params: AttachOIDC,
    options: RequestOptions = {},
  ): Promise<AttachedOIDC> {
    const response = await this.#client.requestWithAuth("/iam/account/oidc", {
      method: "POST",
      body: JSON.stringify(params),
      ...options,
    });

    return (await response.json()) as AttachedOIDC;
  }

  async detachOIDC(options: RequestOptions = {}): Promise<{ domain: string }> {
    const response = await this.#client.requestWithAuth("/iam/account/oidc", {
      method: "DELETE",
      ...options,
    });

    return (await response.json()) as { domain: string };
  }

  async acceptTerms(
    name: keyof Terms,
    version: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/iam/terms", {
      method: "POST",
      body: JSON.stringify({
        name,
        version,
      }),
      ...options,
    });

    return (await response.json()) as StatusResponse;
  }

  async getLogo(options: RequestOptions = {}): Promise<{
    encoded: string;
    contentType: string;
  }> {
    const response = await this.#client.requestWithAuth("/iam/logo", {
      method: "GET",
      ...options,
    });

    return {
      encoded: await response.text(),
      contentType:
        response.headers.get("content-type") || "application/octet-stream",
    };
  }
}
